'use client';
import React, { useEffect, useRef, useState } from 'react';

interface OperatingOption {
  title: string;
  subtitle: string;
  schedule: string;
  description: string;
  goodFor: string[];
  tag: string;
  highlighted: boolean;
}

const operatingOptions: OperatingOption[] = [
  {
    title: 'OTR',
    subtitle: 'Amazon Contract / Long-Haul',
    schedule: '~2–3 Weeks Out',
    description: 'Designed for Owner-Operators who are comfortable spending more time on the road and want access to a broader freight network.',
    goodFor: ['Higher utilization', 'Longer-distance freight', 'Broader operating areas', 'Consistent miles', 'Longer periods on the road'],
    tag: 'Stay Out Longer',
    highlighted: false,
  },
  {
    title: 'Regional',
    subtitle: 'Street & Spot / Contract Near Home',
    schedule: '1–2x Per Week Home',
    description: 'Available for Owner-Operators who prefer to operate within a defined area and stay closer to home. Regional availability depends on current freight demand.',
    goodFor: ['Regional lanes', 'Contract lanes near home state', 'Freight within preferred markets', 'More frequent home time', 'Defined operating area'],
    tag: 'Stay Closer to Home',
    highlighted: true,
  },
];

interface HomeTimeOption {
  want: string;
  program: string;
  detail: string;
  icon: string;
}

const homeTimeOptions: HomeTimeOption[] = [
  {
    icon: '🛣️',
    want: 'Want to Stay Out Longer?',
    program: 'Amazon Contract / OTR',
    detail: 'Typically 2–3 weeks out at a time. Designed for drivers comfortable spending extended periods on the road with access to broader freight opportunities.',
  },
  {
    icon: '🏠',
    want: 'Want to Stay Closer to Home?',
    program: 'Regional / Street & Spot',
    detail: 'Street and regional freight may provide better opportunities for drivers who want to operate closer to home and potentially return home once or twice a week.',
  },
  {
    icon: '📍',
    want: 'Looking for a Contract Near Home?',
    program: 'Contract Lanes',
    detail: 'When available, we can look for contract opportunities operating closer to your home state. Availability depends on current freight and contracts.',
  },
];

const HomeTimeEquipment: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animated, setAnimated] = useState(false);
  const revealRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setAnimated(true);
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <section id="home-time" className="py-32 relative" ref={sectionRef}>
      <div className="beam-border-h" style={{ top: 0, bottom: 'auto' }} />
      <div className="dot-pattern absolute inset-0 opacity-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div
          className="reveal mb-16 grid md:grid-cols-12 gap-8"
          ref={(el) => { revealRefs.current[0] = el; }}>
          <div className="md:col-span-1">
            <span className="section-num">03/</span>
          </div>
          <div className="md:col-span-11 space-y-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.4em] text-violet">
              OTR & Regional · Home-Time Options
            </p>
            <h2 className="text-display font-bold tracking-tight leading-tight text-cream">
              Two Ways to Run.
              <span className="text-silver-lavender/50"> One Goal: Keep Your Truck Moving.</span>
            </h2>
          </div>
        </div>

        {/* OTR vs Regional cards */}
        <div
          className="grid lg:grid-cols-2 gap-8 mb-16 reveal"
          ref={(el) => { revealRefs.current[1] = el; }}>
          {operatingOptions.map((opt) => (
            <div
              key={opt.title}
              className={`border rounded-2xl overflow-hidden ${opt.highlighted ? 'border-violet/40 bg-violet/5' : 'border-silver-lavender/15 bg-plum-deep/40'}`}>
              {/* Card header */}
              <div className={`px-6 py-5 border-b ${opt.highlighted ? 'border-violet/20 bg-violet/10' : 'border-silver-lavender/10 bg-plum-deep/60'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${opt.highlighted ? 'bg-violet/20 text-violet' : 'bg-silver-lavender/10 text-silver-lavender/50'}`}>
                    {opt.tag}
                  </span>
                  <span className="text-[11px] font-bold text-silver-lavender/50">{opt.schedule}</span>
                </div>
                <h3 className="text-2xl font-bold text-cream">{opt.title}</h3>
                <p className="text-sm text-silver-lavender/50 mt-0.5">{opt.subtitle}</p>
              </div>

              {/* Description */}
              <div className="px-6 py-4 border-b border-silver-lavender/8">
                <p className="text-sm text-silver-lavender/65 font-light leading-relaxed">{opt.description}</p>
              </div>

              {/* Good for list */}
              <div className="px-6 py-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-silver-lavender/40 mb-3">Good Fit If You Want:</p>
                <div className="space-y-2">
                  {opt.goodFor.map((item) => (
                    <div key={item} className="flex items-center gap-2.5">
                      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${opt.highlighted ? 'bg-violet' : 'bg-silver-lavender/30'}`} />
                      <span className="text-sm text-silver-lavender/65 font-light">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Home time options */}
        <div
          className="reveal reveal-delay-2"
          ref={(el) => { revealRefs.current[2] = el; }}>
          <div className="mb-8">
            <h3 className="text-xl font-bold text-cream mb-2">Your Home Time Matters</h3>
            <p className="text-sm text-silver-lavender/50 font-light max-w-2xl">
              We understand that not every Owner-Operator wants the same schedule. That&apos;s why we offer
              different freight options depending on how you prefer to operate.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {homeTimeOptions.map((opt) => (
              <div
                key={opt.want}
                className="bg-plum-card border border-silver-lavender/12 rounded-2xl p-6 hover:border-violet/25 transition-all duration-300 space-y-3">
                <span className="text-3xl">{opt.icon}</span>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-silver-lavender/40 mb-1">{opt.want}</p>
                  <p className="text-base font-bold text-violet mb-2">{opt.program}</p>
                  <p className="text-sm text-silver-lavender/60 font-light leading-relaxed">{opt.detail}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="mt-8 bg-plum-deep/60 border border-silver-lavender/10 rounded-xl p-5">
            <p className="text-xs text-silver-lavender/40 leading-relaxed font-light italic">
              Home-time frequency is dependent on the specific freight, lane, contract, and operating plan.
              Regional availability depends on current freight demand and available contracts.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeTimeEquipment;